import React, { useEffect, useRef } from "react";
import { IonContent, IonPage, IonSpinner, IonText } from "@ionic/react";
import { useHistory } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

/**
 * WEB-only landing point for the Cognito Hosted-UI social redirect.
 *
 * ⚠️ Native does not use this. There the callback arrives as the `arcconnect://auth/callback`
 * deep link and is intercepted in App.tsx's `appUrlOpen` handler (D36). On web there is no
 * deep link — Cognito performs a real browser redirect to
 * `${window.location.origin}/oauth/callback`, which is what `SOCIAL_REDIRECT_URI` resolves to
 * for `Capacitor.getPlatform() === "web"`.
 *
 * ⚠️ Without this route the redirect hit AppRoutes' catch-all — `<Route path="*">` →
 * `<Redirect to="/home" />` — so the browser landed on the dashboard and the authorization
 * code was silently discarded. Web social sign-in could never complete, and nothing errored.
 *
 * The PKCE verifier survives the full-page reload because it lives in Preferences, which is
 * localStorage-backed on web. `completeSocialLogin` owns everything after that: state
 * validation, the code exchange, token storage, the post-login batch and navigation — the
 * same function the native path calls, so the two cannot drift (B1).
 */
const OAuthCallback: React.FC = () => {
  const { completeSocialLogin } = useAuth() as any;
  const history = useHistory();
  // React 18 StrictMode double-invokes effects in development. An authorization code is
  // SINGLE-USE, so a second exchange would fail and clobber a sign-in that had succeeded.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (!code) {
      // Cognito reports provider-side failures on this same redirect.
      console.error(
        "OAuth callback carried no code:",
        params.get("error_description") || params.get("error") || "(no detail)",
      );
      history.replace("/signin?register=false");
      return;
    }

    void completeSocialLogin(code, state);
  }, [completeSocialLogin, history]);

  return (
    <IonPage>
      <IonContent fullscreen>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 12,
          }}
        >
          <IonSpinner />
          <IonText>
            <p>Signing you in…</p>
          </IonText>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default OAuthCallback;
